import { useCallback, useEffect, useMemo, useState } from "react";
import {
  assignAdminListingBoundaries,
  getAdminGeoBoundaries,
  getAdminGeoBoundarySummary,
  importAdminGeoBoundaries,
  patchAdminGeoBoundary,
} from "../../../services/midpointApi";
import { CardError, CardLoading } from "../intelligence/CardState";
import AdminActionGroup from "./layout/AdminActionGroup";
import AdminBtn from "./layout/AdminBtn";
import AdminPageHeader from "./layout/AdminPageHeader";
import AdminPageLayout from "./layout/AdminPageLayout";
import AdminStatePanel from "./layout/AdminStatePanel";
import AdminToolbar from "./layout/AdminToolbar";
import RealEstateAdminNav from "./layout/RealEstateAdminNav";

const TYPE_OPTIONS = [
  "",
  "barrio",
  "comuna",
  "municipality",
  "market_zone",
  "custom_zone",
];

const AdminGeoBoundariesPage = ({ onClose }) => {
  const [boundaries, setBoundaries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [officialFilter, setOfficialFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [importPayload, setImportPayload] = useState({
    boundary_type: "barrio",
    source_name: "",
    is_official: true,
    geojsonText: "",
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [boundaryResponse, summaryResponse] = await Promise.all([
        getAdminGeoBoundaries({
          type: typeFilter || undefined,
          q: searchText || undefined,
          active: activeFilter === "inactive" ? "false" : "true",
          limit: 500,
        }),
        getAdminGeoBoundarySummary(),
      ]);
      setBoundaries(boundaryResponse.boundaries || []);
      setSummary(summaryResponse.summary || null);
    } catch (loadError) {
      setBoundaries([]);
      setSummary(null);
      setError(loadError.message || "Unable to load boundaries.");
    } finally {
      setLoading(false);
    }
  }, [activeFilter, searchText, typeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredBoundaries = useMemo(() => {
    return boundaries.filter((entry) => {
      if (officialFilter === "official" && !entry.isOfficial) {
        return false;
      }
      if (officialFilter === "custom" && entry.isOfficial) {
        return false;
      }
      return true;
    });
  }, [boundaries, officialFilter]);

  const handleImport = async () => {
    try {
      setActionMessage("");
      const geojson = JSON.parse(importPayload.geojsonText);
      const result = await importAdminGeoBoundaries({
        geojson,
        boundary_type: importPayload.boundary_type,
        source_name: importPayload.source_name,
        is_official: importPayload.is_official,
      });
      setActionMessage(
        `Import complete: ${result.summary?.inserted || 0} inserted, ${result.summary?.updated || 0} updated.`
      );
      await loadData();
    } catch (importError) {
      setActionMessage(importError.message || "Import failed.");
    }
  };

  const handleAssign = async () => {
    try {
      setActionMessage("");
      const result = await assignAdminListingBoundaries({
        limit: 500,
        only_unassigned: true,
      });
      setActionMessage(
        `Assignment: ${result.summary?.matched || 0} matched, ${result.summary?.no_boundary_found || 0} outside polygons.`
      );
      await loadData();
    } catch (assignError) {
      setActionMessage(assignError.message || "Assignment failed.");
    }
  };

  const toggleActive = async (boundary) => {
    await patchAdminGeoBoundary(boundary.boundaryId, { is_active: !boundary.isActive });
    await loadData();
  };

  const summaryCards = summary
    ? [
        { label: "Total boundaries", value: summary.total },
        { label: "Official", value: summary.official_count },
        { label: "Market / custom zones", value: summary.market_zone_count },
        { label: "Listings assigned", value: summary.listings?.assigned },
        { label: "Listings without boundary", value: summary.listings?.unassigned_with_coords },
      ]
    : [];

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title="Geospatial boundaries"
        subtitle="Import official polygons, assign listings, and manage market zones."
        onClose={onClose}
      />
      <RealEstateAdminNav links={[]} />

      {actionMessage ? <AdminStatePanel variant="info">{actionMessage}</AdminStatePanel> : null}
      {error ? <CardError message={error} /> : null}

      <div className="admin-summary-grid">
        {summaryCards.map((card) => (
          <div key={card.label} className="admin-summary-card">
            <span className="admin-summary-label">{card.label}</span>
            <strong>{card.value ?? "—"}</strong>
          </div>
        ))}
      </div>

      <AdminToolbar>
        <label>
          Type
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            {TYPE_OPTIONS.map((value) => (
              <option key={value || "all"} value={value}>
                {value || "All types"}
              </option>
            ))}
          </select>
        </label>
        <label>
          Official
          <select value={officialFilter} onChange={(event) => setOfficialFilter(event.target.value)}>
            <option value="">All</option>
            <option value="official">Official</option>
            <option value="custom">Custom / market</option>
          </select>
        </label>
        <label>
          Status
          <select value={activeFilter} onChange={(event) => setActiveFilter(event.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
        <label>
          Search
          <input
            type="search"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Boundary name"
          />
        </label>
        <AdminActionGroup>
          <AdminBtn onClick={loadData}>Refresh</AdminBtn>
          <AdminBtn onClick={handleAssign}>Run listing assignment</AdminBtn>
        </AdminActionGroup>
      </AdminToolbar>

      <div className="admin-two-column">
        <section className="admin-panel">
          <h3>Import GeoJSON</h3>
          <label>
            Boundary type
            <select
              value={importPayload.boundary_type}
              onChange={(event) =>
                setImportPayload((current) => ({
                  ...current,
                  boundary_type: event.target.value,
                }))
              }
            >
              {TYPE_OPTIONS.filter(Boolean).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Source name
            <input
              value={importPayload.source_name}
              onChange={(event) =>
                setImportPayload((current) => ({ ...current, source_name: event.target.value }))
              }
            />
          </label>
          <label className="layer-toggle">
            <input
              type="checkbox"
              checked={importPayload.is_official}
              onChange={(event) =>
                setImportPayload((current) => ({ ...current, is_official: event.target.checked }))
              }
            />
            Official source
          </label>
          <label>
            GeoJSON
            <textarea
              rows={8}
              value={importPayload.geojsonText}
              onChange={(event) =>
                setImportPayload((current) => ({ ...current, geojsonText: event.target.value }))
              }
              placeholder='{ "type": "FeatureCollection", "features": [] }'
            />
          </label>
          <AdminBtn onClick={handleImport}>Import</AdminBtn>
        </section>

        <section className="admin-panel admin-table-panel">
          {loading ? <CardLoading label="Loading boundaries" /> : null}
          {!loading ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Source</th>
                  <th>Official</th>
                  <th>Confidence</th>
                  <th>Active</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredBoundaries.map((boundary) => (
                  <tr key={boundary.boundaryId}>
                    <td>{boundary.displayName || boundary.name}</td>
                    <td>{boundary.boundaryType}</td>
                    <td>{boundary.sourceName || "—"}</td>
                    <td>{boundary.isOfficial ? "Yes" : "No"}</td>
                    <td>{boundary.confidenceScore}</td>
                    <td>{boundary.isActive ? "Yes" : "No"}</td>
                    <td>{boundary.updatedAt ? new Date(boundary.updatedAt).toLocaleString() : "—"}</td>
                    <td>
                      <button type="button" className="text-button" onClick={() => setSelected(boundary)}>
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      </div>

      {selected ? (
        <div className="admin-drawer">
          <AdminPageHeader
            title={selected.displayName || selected.name}
            subtitle={selected.boundaryType}
            onClose={() => setSelected(null)}
          />
          <p>Parent: {selected.parentName || "—"}</p>
          <p>Source: {selected.sourceName || "—"}</p>
          <p>Confidence: {selected.confidenceScore}</p>
          <pre className="admin-metadata-preview">
            {JSON.stringify(selected.metadata || {}, null, 2)}
          </pre>
          <AdminActionGroup>
            <AdminBtn onClick={() => toggleActive(selected)}>
              {selected.isActive ? "Deactivate" : "Activate"}
            </AdminBtn>
            <AdminBtn
              onClick={() => {
                window.location.hash = "#/";
                window.dispatchEvent(new HashChangeEvent("hashchange"));
              }}
            >
              View on map
            </AdminBtn>
          </AdminActionGroup>
        </div>
      ) : null}
    </AdminPageLayout>
  );
};

export default AdminGeoBoundariesPage;
